import React, { useMemo, useState, useContext } from 'react';
import { Sidebar,Menu } from 'react-pro-sidebar';
import {  Box,Divider,useTheme,Button} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { SidebarItem } from './SidebarItems';
import { ThemePicker } from './ThemePicker';
import Logo from './Logo';
import { AuthContext } from '../auth/AuthContext';
import UserAvatar from './UserAvatar';
// Icons
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BoltIcon from '@mui/icons-material/Bolt';
import { getAuth, signOut } from "firebase/auth";
import { useImpersonateDesignFirm } from '../ImpersonateDesignFirm';
import { Permissions } from 'ts-interfaces';
import { firebaseHelper, usePermissions } from 'ui';


const drawerWidth = 280;

interface SideNavProps {
  open: boolean;
  onClose: () => void;
  mobileView?: boolean;
}

interface NavRoute {
  title: string;
  path: string;
  icon: JSX.Element;
  isVisible: (params: RouteVisibilityParams) => boolean;
}

const isAdmin=false;

interface RouteVisibilityParams {
  isAdmin: boolean;
  isDesigner: boolean;
}

const navRoutesSuperuser: NavRoute[] = [
  {
    title: "Design Firms",
    path: "/pr/firms",
    icon: <BusinessCenterIcon />,
    isVisible: ({ isAdmin }) => true
  },
]


const navRoutes: NavRoute[] = [
  {
    title: "Teams",
    path: "/pr/teams",
    icon: <PeopleIcon />,
    isVisible: () => true // Visible to all users
  },
  {
    title: "Billing",
    path: "/pr/billing",
    icon: <ReceiptIcon />,
    isVisible: ({ isDesigner }) => true
  },
  {
    title: "Credits",
    path: "/pr/credits",
    icon: <BoltIcon />,
    isVisible: () => true
  },
  {
    title: "Settings",
    path: "/pr/settings",
    icon: <SettingsIcon />,
    isVisible: ({ isDesigner }) => true
  }
];

export const SideNav: React.FC<SideNavProps> = ({ open, onClose, mobileView = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser }  = useContext(AuthContext);
  const { permissions, loading, error }: { permissions: Permissions | null; loading: boolean; error: Error | null } = usePermissions(currentUser?.uid, firebaseHelper.db);

  const superuser = currentUser && permissions?.teamsSuperuser===true;
  const teamMembers = [];
  const isDesigner = (currentUser as any)?.roles?.includes('designer') || false;
  const impersonateDesignFirm = useImpersonateDesignFirm( (state) => state.impersonate_firm);
  const routes = superuser===true && impersonateDesignFirm==null ? navRoutesSuperuser : navRoutes;

  const [selected, setSelected] = useState(
    location.pathname.split('/').pop() || 'team'
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    if (mobileView) {
      onClose();
    }
  };

  const menuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
    active: {
      color: theme.palette.primary.contrastText,
      backgroundColor: `${theme.palette.primary.main}!important`,
    },
    icon: {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
    },
    button: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
    },
  };

 //if (currentUser==null ||   (isFirmAdmin(customClaims) == false && isSuperuser(customClaims) ==false)  ) { return (null) }
  return (
    <Sidebar
      breakPoint="md"
      transitionDuration={300}
      onBackdropClick={onClose}
      collapsed={!open}
      backgroundColor={theme.palette.background.paper}
      style={{
        height: '100vh',
        borderRight: `1px solid ${theme.palette.divider}`,
        flexWrap: 'nowrap',
        width:'300px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Logo size="small" />
          <ThemePicker />
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          pb: 2
        }}>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Menu menuItemStyles={menuItemStyles}>
          {routes.map((route) => 
              <SidebarItem
                key={route.path}
                title={route.title}
                to={route.path}
                icon={route.icon}
                selected={selected}
                setSelected={setSelected}
              />            
          )}
        </Menu>
        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ p: 2 }}>
          <UserAvatar
            isSuperuser={superuser || false}
            user={currentUser}
            containerSx={{ mb: 2 }}
          />

          <Button
            variant="outlined"
            color="primary"
            fullWidth
            sx={{ mb: 2 }}
            onClick={() =>  {
              signOut(getAuth()).then(() => {
                useImpersonateDesignFirm.setState({ impersonate_firm: null });
                sessionStorage.clear();
                navigate('/');
              });
            }}
          >
            Sign Out
          </Button>
        </Box>
      </Box>
    </Sidebar>
  );
};

export default SideNav;